"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, Search, CircleCheck as CheckCircle2, ArrowRight, Flame, Wrench, ShieldCheck, Clock } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const processSteps = [{
  stepNumber: "01",
  title: "Tell Us What's Happening",
  subtitle: "Quick intake & dispatch",
  description: "Call our 24/7 Jonesboro dispatch or send an urgent online request describing leaks, strange noises, or cold water issues.",
  icon: PhoneCall,
  actionNote: "Average dispatch time: Under 15 mins"
}, {
  stepNumber: "02",
  title: "Diagnose the Water Heater",
  subtitle: "On-site system inspection",
  description: "A licensed, insured plumber thoroughly evaluates the heating elements, thermostat, valves, and tank integrity on-site.",
  icon: Search,
  actionNote: "Comprehensive safety inspection"
}, {
  stepNumber: "03",
  title: "Repair or Replacement",
  subtitle: "Clear, upfront solutions",
  description: "We explain honest repair vs. replacement options with transparent pricing upfront so you make the best decision for your home.",
  icon: CheckCircle2,
  actionNote: "Guaranteed workmanship & parts"
}];
export default function WaterHeaterProcess() {
  const shouldReduceMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: shouldReduceMotion ? 0 : i * 0.15,
        duration: 0.5,
        ease: "easeOut"
      }
    })
  };
  return <section id="waterheaterserviceprocess" data-nav="light" className="bg-background text-foreground py-16 md:py-24 transition-colors">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-18">
          <div className="flex justify-center mb-4">
            <Badge variant="outline" className="border-accent/40 bg-accent/10 text-accent-foreground px-3.5 py-1 text-xs font-semibold uppercase tracking-wider rounded-full flex items-center gap-2">
              <Flame className="size-3.5 text-accent shrink-0" />
              <span>Step-by-Step Restoration</span>
            </Badge>
          </div>

          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            How Our Water Heater Service Works
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            From the moment your hot water stops working to completed repairs or installations, our licensed Jonesboro technicians keep the entire process fast, transparent, and hassle-free.
          </p>
        </div>

        {/* 3-Step Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 relative">
          {processSteps.map((step, index) => {
          const IconComponent = step.icon;
          return <motion.div key={step.stepNumber} data-index={index} custom={index} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.2
          }} variants={cardVariants} className="relative flex flex-col h-full">
                <Card className="flex-1 bg-card border border-border shadow-md rounded-2xl relative overflow-hidden transition-[border-color,box-shadow] hover:border-accent/50 hover:shadow-lg">
                  {/* Step indicator pill */}
                  <div className="absolute top-5 right-5">
                    <span className="font-mono text-xs font-bold px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground border border-border">
                      STEP {step.stepNumber}
                    </span>
                  </div>

                  <CardHeader className="pt-8 pb-3 px-6 sm:px-7">
                    {/* Squircle Icon */}
                    <div className="size-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-5 border border-primary/20">
                      <IconComponent className="size-7 stroke-[2.2]" />
                    </div>

                    <CardTitle className="text-xl sm:text-2xl font-bold font-default text-card-foreground">
                      {step.title}
                    </CardTitle>
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mt-1">
                      {step.subtitle}
                    </p>
                  </CardHeader>

                  <CardContent className="px-6 sm:px-7 pb-8 pt-2 flex flex-col justify-between flex-1">
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                      {step.description}
                    </p>

                    <div className="pt-4 border-t border-border/70 flex items-center gap-2 text-xs font-medium text-foreground">
                      <ShieldCheck className="size-4 text-accent shrink-0" />
                      <span>{step.actionNote}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Desktop Directional Visual Indicator between items */}
                {index < processSteps.length - 1 && <div className="hidden md:flex absolute -right-3 lg:-right-4 top-1/2 -translate-y-1/2 z-10 size-7 lg:size-8 rounded-full bg-background border border-border shadow-xs items-center justify-center text-muted-foreground pointer-events-none">
                    <ArrowRight className="size-3.5 lg:size-4" />
                  </div>}
              </motion.div>;
        })}
        </div>

        {/* Bottom Guarantee Banner & Emergency Action Cluster */}
        <div className="mt-12 sm:mt-16 rounded-2xl bg-secondary/60 border border-border p-6 sm:p-8 lg:p-10 flex flex-col lg:flex-row items-center justify-between gap-6 shadow-sm">
          <div className="flex items-start sm:items-center gap-4 text-left">
            <div className="size-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0 shadow-sm">
              <Clock className="size-6" />
            </div>
            <div>
              <h3 className="font-default text-lg sm:text-xl font-bold text-foreground">
                Need urgent hot water restoration in Jonesboro?
              </h3>
              <p className="text-muted-foreground text-sm mt-0.5">
                Our emergency repair trucks are stocked with standard replacement parts for fast same-day service.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 w-full lg:w-auto shrink-0">
            <a href="tel:4708479076" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground font-semibold hover:bg-accent/90 shadow-sm rounded-xl px-6 h-12">
                <PhoneCall className="size-4 mr-2" />
                <span>Call (470) 847-9076</span>
              </Button>
            </a>

            <Button asChild variant="default" size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground font-semibold hover:bg-primary/90 shadow-sm rounded-xl px-6 h-12">
              <Link to="/contact/">
                <Wrench className="size-4 mr-2" />
                <span>Request Service</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}